import React, { useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Content, Item, Trigger } from '@radix-ui/react-accordion';

import { faAngleDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { NULL_ADDRESS } from '@soundxyz/utils/const';
import {
  PlaylistEmbedUrl,
  PlaylistIframeEmbed,
  ReleaseEmbedUrl,
  ReleaseIframeEmbed,
} from '@soundxyz/utils/constants/embeds';
import { keyframes, styled } from '@components-shared/src/stitches';
import { Button } from '@components/main-components/button/Button';
import { SquareSelector } from '@components/main-components/selectors/square-selector';
import { DeprecatedDynamicSkeleton } from '@components/main-components/skeleton/Skeleton';
import { useAuth } from '@context/auth/base';
import { MediaQuery } from '@context/mediaQuery';
import { ToastContext } from '@context/toast';
import { trackClickEvent } from '@lib/analytics';

export const EmbedBody = ({
  type,
  releaseId,
  playlistId,
  className,
}:
  | {
      type: 'drop';
      releaseId: string;
      playlistId?: undefined;
      className?: string;
    }
  | {
      type: 'playlist';
      playlistId: string;
      releaseId?: undefined;
      className?: string;
    }) => {
  const { openToast } = ToastContext.useContainer();
  const { tablet } = MediaQuery.useContainer();
  const { authUserAddress: referralAddress = null } = useAuth();
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [expanded, setExpanded] = useState('');

  const iframeRef = useRef<HTMLDivElement>(null);

  const height = type === 'drop' ? '188px' : '355px';

  const trackEvent = () =>
    type == 'drop'
      ? trackClickEvent({
          event: 'Copy code for embed',
          properties: {
            associationId: releaseId,
            associationType: 'Release',
            referralAddress: (referralAddress || NULL_ADDRESS).toLowerCase(),
          },
        })
      : trackClickEvent({
          event: 'Copy code for embed',
          properties: {
            associationId: playlistId,
            associationType: 'Playlist',
            referralAddress: (referralAddress || NULL_ADDRESS).toLowerCase(),
          },
        });

  const iframeUrl =
    type === 'drop'
      ? ReleaseEmbedUrl({
          releaseId,
          referral: {
            referralAddress,
            source: {
              embed: 'sound',
            },
          },
        })
      : PlaylistEmbedUrl({
          playlistId,
          theme,
        });

  const IFrame =
    type === 'drop'
      ? ReleaseIframeEmbed({
          releaseId,
          html: {
            width: '100%',
            height,
            style: 'border-radius: 8px;',
          },
          url: iframeUrl,
        })
      : PlaylistIframeEmbed({
          playlistId,
          html: {
            width: '100%',
            height,
            style: 'border-radius: 8px; box-shadow: 0px 6px 16px 1px rgba(0, 0, 0, 0.08);',
          },
          theme,
          url: iframeUrl,
        });

  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    setIframeLoaded(false);
  }, [IFrame]);

  useEffect(() => {
    const iframeRefCurrent = iframeRef.current;

    if (!iframeRefCurrent) return;

    const iframeChild = iframeRefCurrent.firstChild;

    const loadListener = () => setIframeLoaded(true);

    iframeChild?.addEventListener('load', loadListener);

    return () => {
      iframeChild?.removeEventListener('load', loadListener);
    };
  }, [IFrame]);

  const onCopyClick = async () => {
    await trackEvent();
    navigator.clipboard
      .writeText(IFrame)
      .then(() => {
        openToast({
          text: 'Copied to clipboard',
          variant: 'success',
        });
      })
      .catch(() => {
        openToast({
          text: 'Failed to copy to clipboard',
          variant: 'error',
        });
      });
  };
  return (
    <div className={className}>
      {type === 'playlist' && (
        <ThemeWrapper>
          <h3>Theme</h3>
          <SquareSelector
            items={[
              { id: 'light', background: 'white' },
              { id: 'dark', background: '$base900' },
            ]}
            initialSelectedId="light"
            onChange={id => {
              if (id === 'light' || id === 'dark') setTheme(id);
            }}
            size={tablet ? 'large' : 'small'}
          />
        </ThemeWrapper>
      )}
      {!iframeLoaded && (
        <DeprecatedDynamicSkeleton
          css={{
            marginBottom: `-${height}`,
            zIndex: '$above1',
          }}
          width="100%"
          height={height}
          borderRadius="8px"
        />
      )}
      <div className="m-0 p-0" ref={iframeRef} dangerouslySetInnerHTML={{ __html: IFrame }} />
      <Accordion
        type="single"
        defaultValue=""
        value={expanded}
        onValueChange={setExpanded}
        collapsible
      >
        <Item className="flex flex-col gap-0" value="embed">
          <StyledTrigger>
            <span className="text-base-l font-semibold">Show code</span>
            <div className="item flex h-6 w-6 items-center justify-center">
              <FontAwesomeIcon icon={faAngleDown} size="lg" />
            </div>
          </StyledTrigger>
          <StyledContent>
            <code>{IFrame}</code>
          </StyledContent>
        </Item>
      </Accordion>
      <div className="mt-6 flex justify-end gap-3">
        <Button label="Copy Code" variant="primary" onClick={onCopyClick} fullWidth />
      </div>
    </div>
  );
};

const ThemeWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 24,

  h3: {
    base: 'M',
    '@tablet': {
      base: 'L',
      fontStyle: 'semibold',
    },
  },
});

const slideDown = keyframes({
  '0%': { height: 0 },
  '100%': { height: 'var(--radix-accordion-content-height)' },
});

const slideUp = keyframes({
  '0%': { height: 'var(--radix-accordion-content-height)' },
  '100%': { height: 0 },
});

const StyledContent = styled(Content, {
  wordBreak: 'break-all',
  overflow: 'hidden',
  backgroundColor: '$neutral100',
  borderRadius: 8,
  padding: 8,

  "&[data-state='open']": {
    animation: `${slideDown} 300ms ease-out`,
  },
  "&[data-state='closed']": {
    animation: `${slideUp} 300ms ease-out`,
  },
});

const StyledTrigger = styled(Trigger, {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'start',
  height: '2rem',
  marginTop: 24,

  svg: {
    transition: 'transform 300ms cubic-bezier(0.87, 0, 0.13, 1)',
  },

  "&[data-state='open']": {
    svg: {
      transform: 'rotate(180deg)',
    },
  },
});
